import { patchSPDataWithParams } from './services'
import { getAllRolesInBoolean, getUserRoles } from 'src/mentor/layout/utils'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

/**
 * Patient Monitoring Service
 *
 * Endpoints (as per support-patient-monitoring-apis.md):
 * - Support portal roles: PATCH /api/secure/cc/support/patient/monitoring
 *
 * Allowed eventTypes by role:
 * - L2/L3 Therapy Support: screening, therapy-session, assessment
 * - Clinic Success Manager/Admin: screening, therapy-session, assessment, consultation
 *
 * Actions (from session logging - consumer/mentor portals):
 * - JOIN: User joins session
 * - LEAVE: User leaves session
 * - REFRESH: Periodic heartbeat (every 3 min)
 * - DISCONNECTED: User disconnected unexpectedly
 * - MARKED_STALE: Session marked as stale
 * - THROWN_OUT: User was removed from session
 * - FOUND_ABSENT: User was found absent
 */

// Event types allowed for L2/L3 therapy support
export const THERAPY_SUPPORT_EVENT_TYPES = [
  { value: 'screening', label: 'Screening' },
  { value: 'therapy-session', label: 'Therapy Session' },
  { value: 'assessment', label: 'Assessment' },
]

// Consultation is available only for clinic success roles.
export const CLINIC_SUCCESS_EVENT_TYPES = [
  { value: 'consultation', label: 'Consultation' },
  { value: 'screening', label: 'Screening' },
  { value: 'therapy-session', label: 'Therapy Session' },
  { value: 'assessment', label: 'Assessment' },
]

// Monitoring scope options
export const MONITORING_SCOPE_OPTIONS = [
  { value: 'PATIENT_ONLY', label: 'Patient Only' },
  { value: 'PATIENT_AND_MENTOR', label: 'Patient & Mentor' },
]

// Monitoring policy options
export const MONITORING_POLICY_OPTIONS = [
  { value: 'FIRST_PER_ACTION', label: 'First Per Action' },
  { value: 'ALL', label: 'All' },
]

// Event modes
export const EVENT_MODE_OPTIONS = [
  { value: 'aumhum', label: 'Aumhum (Online)' },
  { value: 'offline', label: 'Offline' },
  { value: 'home-visit', label: 'Home Visit' },
]

// Actions to monitor (from session logging in consumer/mentor portals)
// Note: REFRESH is excluded as it's an internal heartbeat, not a reportable event
export const MONITORING_ACTION_OPTIONS = [
  { value: 'JOIN', label: 'Join Session' },
  { value: 'LEAVE', label: 'Leave Session' },
  { value: 'JOIN_AGORA_CALL', label: 'Join Video Call' },
  { value: 'LEAVE_AGORA_CALL', label: 'Leave Video Call' },
  { value: 'AUTO_CONNECT_PAUSED', label: 'Auto-Connect Paused' },
  { value: 'AUTO_CONNECT_RESUMED', label: 'Auto-Connect Resumed' },
  { value: 'CAMERA_ON', label: 'Camera On' },
  { value: 'CAMERA_OFF', label: 'Camera Off' },
  { value: 'MIC_ON', label: 'Mic On' },
  { value: 'MIC_OFF', label: 'Mic Off' },
  { value: 'DISCONNECTED', label: 'Disconnected' },
  { value: 'MARKED_STALE', label: 'Marked Stale' },
  { value: 'THROWN_OUT', label: 'Thrown Out' },
  { value: 'FOUND_ABSENT', label: 'Found Absent' },
  { value: 'CHECKIN', label: 'Check-in' },
]

/**
 * Get the support patient monitoring endpoint.
 *
 * @returns {string} API endpoint path
 */
export function getMonitoringApiEndpoint() {
  return '/api/secure/cc/support/patient/monitoring'
}

function hasClinicSuccessMonitoringRole() {
  const roles = getUserRoles() || []
  return (
    roles.includes('ROLE_CLINIC_SUCCESS_MANAGER') ||
    roles.includes('ROLE_CLINIC_SUCCESS_ADMIN')
  )
}

/**
 * Get allowed event types based on user's role
 * @returns {Array} Array of event type options
 */
export function getAllowedEventTypes() {
  const allRoles = getAllRolesInBoolean()

  // Clinic success roles can monitor consultation in addition to shared support event types.
  if (hasClinicSuccessMonitoringRole()) {
    return CLINIC_SUCCESS_EVENT_TYPES
  }

  // L2/L3 therapy support and admins can only use the shared support event types.
  if (allRoles.isL2TherapySupport || allRoles.isL3TherapySupport || allRoles.isAdmin) {
    return THERAPY_SUPPORT_EVENT_TYPES
  }

  return THERAPY_SUPPORT_EVENT_TYPES
}

/**
 * Check if user can manage patient monitoring
 * @returns {boolean} True if user has monitoring permission
 */
export function canManageMonitoring() {
  const allRoles = getAllRolesInBoolean()
  return (
    allRoles.isL2TherapySupport ||
    allRoles.isL3TherapySupport ||
    allRoles.isClinicSuccessManager ||
    allRoles.isClinicSuccessAdmin ||
    allRoles.isAdmin
  )
}

/**
 * Get the user's role type for display purposes
 * @returns {string} Role display name
 */
export function getMonitoringRoleType() {
  const allRoles = getAllRolesInBoolean()

  if (allRoles.isAdmin) return 'Admin'
  if (allRoles.isClinicSuccessAdmin) return 'Clinic Success Admin'
  if (allRoles.isClinicSuccessManager) return 'Clinic Success Manager'
  if (allRoles.isL3TherapySupport) return 'Therapy Support L3'
  if (allRoles.isL2TherapySupport) return 'Therapy Support L2'

  return 'Support'
}

/**
 * Update patient monitoring settings
 *
 * @param {Object} params - Monitoring settings
 * @param {string} params.patientId - Patient ID (required)
 * @param {boolean} [params.monitored] - Enable/disable monitoring
 * @param {string} [params.scope] - PATIENT_ONLY or PATIENT_AND_MENTOR
 * @param {string} [params.policy] - FIRST_PER_ACTION or ALL
 * @param {string[]} [params.eventModes] - Event modes to monitor
 * @param {string[]} [params.eventTypes] - Event types to monitor
 * @param {string[]} [params.actions] - Actions to monitor
 *
 * @returns {Promise<Object>} Response with updated patientInfo
 */
export async function updatePatientMonitoring(params) {
  if (!params.patientId) {
    throw new Error('patientId is required')
  }

  const endpoint = getMonitoringApiEndpoint()

  // Build request body
  const requestBody = {
    patientId: params.patientId,
  }

  // Only include fields that are provided
  if (typeof params.monitored === 'boolean') {
    requestBody.monitored = params.monitored
  }
  if (params.scope) {
    requestBody.scope = params.scope
  }
  if (params.policy) {
    requestBody.policy = params.policy
  }
  if (params.eventModes && params.eventModes.length > 0) {
    requestBody.eventModes = params.eventModes
  }
  if (params.eventTypes && params.eventTypes.length > 0) {
    requestBody.eventTypes = params.eventTypes
  }
  if (params.actions && params.actions.length > 0) {
    requestBody.actions = params.actions
  }

  try {
    const response = await patchSPDataWithParams(endpoint, requestBody)

    // Check if it's an axios error response
    if (response?.isAxiosError || response?.response) {
      const errorResponse = response.response || response
      const errorMessage = errorResponse?.data?.message || errorResponse?.message || 'API request failed'
      return {
        success: false,
        message: errorMessage,
      }
    }

    if (response?.status === 200) {
      // Check if the response data indicates success
      const isSuccess = isCommonResponseSuccessfulV2(response)
      if (isSuccess) {
        const dataMap = getDataMapFromCommonResponse(response)
        return {
          success: true,
          patientInfo: dataMap?.patientInfo || dataMap?.PatientInfo || dataMap,
          message: params.monitored ? 'Monitoring enabled' : 'Monitoring settings updated',
        }
      } else {
        const message = response?.data?.message || 'Operation was not successful'
        return {
          success: false,
          message,
        }
      }
    }

    return {
      success: false,
      message: response?.data?.message || 'Failed to update monitoring settings',
    }
  } catch (error) {
    return {
      success: false,
      message: error?.response?.data?.message || error?.message || 'Failed to update monitoring settings',
    }
  }
}

/**
 * Enable monitoring for a patient with default settings
 * @param {string} patientId - Patient ID
 * @returns {Promise<Object>} Response with updated patientInfo
 */
export async function enablePatientMonitoring(patientId) {
  const allowedEventTypes = getAllowedEventTypes()

  return updatePatientMonitoring({
    patientId,
    monitored: true,
    scope: 'PATIENT_ONLY',
    policy: 'FIRST_PER_ACTION',
    eventModes: ['aumhum'],
    eventTypes: allowedEventTypes.map((et) => et.value),
    actions: [
      'JOIN',
      'LEAVE',
      'JOIN_AGORA_CALL',
      'LEAVE_AGORA_CALL',
      'AUTO_CONNECT_PAUSED',
      'AUTO_CONNECT_RESUMED',
      'DISCONNECTED',
    ],
  })
}

/**
 * Disable monitoring for a patient
 * @param {string} patientId - Patient ID
 * @returns {Promise<Object>} Response with updated patientInfo
 */
export async function disablePatientMonitoring(patientId) {
  return updatePatientMonitoring({
    patientId,
    monitored: false,
  })
}
